import { assetClassificationOptionsSchema, assetCreatorOptionSchema, assetItemSchema, assetPageSchema, assetThumbnailSchema, } from '@/entities/asset/model/asset';
import { assetRecyclePageSchema } from '@/entities/asset/model/assetRecycle';
import { httpClient } from '@/shared/api/httpClient';
import { readApiEnvelopeData } from '@/shared/api/response';
import { z } from 'zod';
/** 素材列表查询缓存键，筛选条件与页码由调用方继续追加。 */
export const ASSET_LIST_QUERY_KEY = ['assets'];
/** 素材缩略图状态查询缓存键，素材 ID 集合由调用方继续追加。 */
export const ASSET_THUMBNAILS_QUERY_KEY = ['asset-thumbnails'];
/** 分页查询当前用户素材。 */
export async function fetchAssetPage(query) {
    const response = await httpClient.get('/asset/page', { params: query });
    return assetPageSchema.parse(readApiEnvelopeData(response));
}
/** 项目素材库单页条数，与素材分页接口单页上限保持一致。 */
export const PROJECT_MATERIAL_PAGE_SIZE = 100;
/**
 * 查询指定项目素材库的未删除素材。
 * 已删除素材不在该接口返回，回收站列表请改用 {@link fetchAssetRecyclePage}。
 * @param projectId 目标项目 ID。
 * @param pageNum 页码，从 1 开始。
 * @param pageSize 每页条数。
 * @param keyword 素材名称关键字，交由服务端过滤。
 * @returns 素材分页数据。
 */
export async function fetchProjectMaterialPage(projectId, pageNum = 1, pageSize = PROJECT_MATERIAL_PAGE_SIZE, keyword) {
    return fetchAssetPage({
        projectId,
        includeDeleted: '0',
        // 搜索关键字交给后端服务端过滤，避免依赖已加载分页、遗漏后续页数据。
        assetName: keyword?.trim() ?? undefined,
        pageNum,
        pageSize,
    });
}
/**
 * 上传素材到指定项目素材库。
 * @param input 待上传文件、目标项目、批次媒体类型与图片主体类型。
 * @returns 新建的素材列表。
 */
export async function uploadProjectMaterials(input) {
    return uploadAssets({
        files: input.files,
        assetDataType: input.assetDataType,
        /** 素材归属 story 对应剧情素材（1）、game 对应游戏素材（2），按此映射分类编码。 */
        assetCategory: input.materialScope === 'game' ? '2' : '1',
        ...(input.assetSubjectType ? { assetSubjectType: input.assetSubjectType } : {}),
        projectId: input.projectId,
    });
}
/** 查询编辑器当前项目可见的剧情素材与游戏素材。 */
export async function fetchEditorAssetPage(query) {
    const response = await httpClient.get('/asset/editor/page', { params: query });
    return assetPageSchema.parse(readApiEnvelopeData(response));
}
/**
 * 查询指定项目素材的创建人筛选项。
 *
 * 服务端按项目聚合未删除素材的创建人并带上素材数量，供素材选择弹窗的「创建人」下拉使用；
 * 选中项以 userId 回传给素材分页接口完成服务端过滤。
 * @param projectId 项目 ID。
 * @returns 创建人及其素材数量，按素材数量倒序。
 */
export async function fetchAssetCreatorOptions(projectId) {
    const response = await httpClient.get('/asset/editor/creator-options', {
        params: { projectId },
    });
    return assetCreatorOptionSchema.array().parse(readApiEnvelopeData(response));
}
/**
 * 编辑器素材库单页条数。
 *
 * 素材面板首屏只取一页，滚到底再追加下一页；单页过大会让首屏白等整批素材，
 * 也会让素材多的项目一次拉回大量详情数据。
 */
export const EDITOR_ASSET_PAGE_SIZE = 10;
/**
 * 查询素材分类与媒体类型等枚举选项。
 * 用于按服务端统一顺序渲染筛选项，并生成素材归属的展示文案。
 * @returns 分类、媒体类型、主体类型与来源选项。
 */
export async function fetchAssetClassificationOptions() {
    const response = await httpClient.get('/asset/classification/options');
    return assetClassificationOptionsSchema.parse(readApiEnvelopeData(response));
}
/** 查询当前用户素材详情。 */
export async function fetchAssetDetail(id) {
    const response = await httpClient.get('/asset/detail', { params: { id } });
    return assetItemSchema.parse(readApiEnvelopeData(response));
}
/** 重命名当前用户素材。 */
export async function renameAsset(id, assetName) {
    const response = await httpClient.post('/asset/rename', { id, assetName });
    return assetItemSchema.parse(readApiEnvelopeData(response));
}
/** 保存素材详情；用户上传素材的 generationPrompt 会由服务端忽略。 */
export async function updateAsset(input) {
    const response = await httpClient.post('/asset/update', input);
    return assetItemSchema.parse(readApiEnvelopeData(response));
}
/** 切换单个素材收藏状态。 */
export async function toggleAssetCollect(id) {
    const response = await httpClient.post('/asset/collect/toggle', { id });
    return assetItemSchema.parse(readApiEnvelopeData(response));
}
/** 将选中素材统一设置为目标收藏状态。 */
export async function updateAssetBatchCollect(ids, collected) {
    await httpClient.post('/asset/collect/batch', { ids, collected });
}
/** 批量素材接口单次提交上限，与后端 AssetBatchRequestDTO 的 @Size(max = 100) 保持一致。 */
const ASSET_BATCH_LIMIT = 100;
/** 当前删除前钩子；没有队列时直接结束。 */
let beforeDeleteAssets = () => Promise.resolve();
/**
 * 注册删除素材前的清理动作。后注册的覆盖先注册的。
 * @param hook 按素材 ID 中止后台上传
 */
export function registerBeforeDeleteAssets(hook) {
    beforeDeleteAssets = hook;
}
/**
 * 批量删除素材；素材进入素材回收站并保留 30 天，期间可以恢复。
 *
 * 后端单次最多处理 100 条，超过部分按 100 条切分顺序提交。
 * 任一批失败会中断后续提交，已成功的批次不会回滚，调用方必须在失败后刷新列表。
 * @param ids 待删除素材 ID。
 */
export async function deleteAssets(ids) {
    const uniqueIds = [...new Set(ids)];
    await beforeDeleteAssets(uniqueIds);
    for (let start = 0; start < uniqueIds.length; start += ASSET_BATCH_LIMIT) {
        await httpClient.post('/asset/delete', {
            ids: uniqueIds.slice(start, start + ASSET_BATCH_LIMIT),
        });
    }
}
/** 复制素材到当前用户拥有的目标项目。 */
export async function copyAssetToProject(id, projectId) {
    const response = await httpClient.post('/asset/copy', { id, projectId });
    return assetItemSchema.parse(readApiEnvelopeData(response));
}
/** 未指定来源时上传接口提交的默认来源：2-用户上传。 */
const UPLOAD_ASSET_SOURCE_TYPE = '2';
/** 占位素材提交的素材来源：1-AI 生成；生成提示词只在该来源下才会被服务端保存与返回。 */
const GENERATED_ASSET_SOURCE_TYPE = '1';
/** 上传一个或多个用户素材文件；同一批文件必须属于同一种媒体类型。 */
export async function uploadAssets(params) {
    const formData = new FormData();
    (params.files ?? []).forEach((file) => {
        formData.append('files', file, file.name);
    });
    // 素材来源为服务端必填项，未指定时按用户上传提交，避免请求被参数校验拦截。
    formData.append('assetType', params.assetType ?? UPLOAD_ASSET_SOURCE_TYPE);
    formData.append('assetDataType', params.assetDataType);
    formData.append('assetCategory', params.assetCategory);
    if (params.assetSubjectType)
        formData.append('assetSubjectType', params.assetSubjectType);
    if (params.projectId != null)
        formData.append('projectId', String(params.projectId));
    if (params.assetName?.trim())
        formData.append('assetName', params.assetName.trim());
    const response = await httpClient.post('/asset/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });
    return assetItemSchema.array().parse(readApiEnvelopeData(response));
}
/** 分片初始化结果。 */
const multipartInitSchema = z.object({
    /** OSS 分片任务号。 */
    uploadId: z.string().min(1),
    /** 本次上传的对象键。 */
    objectKey: z.string().min(1),
    /** 分片字节数。 */
    partSize: z.number().int().positive(),
    /** 分片总数。 */
    partCount: z.number().int().positive(),
});
/** 服务端确认已经写入 OSS 的分片。 */
const multipartUploadPartSchema = z.object({
    /** 分片号。 */
    partNumber: z.number().int().positive(),
    /** OSS 分片校验标识，用于现有断点记录和合并。 */
    etag: z.string().min(1),
});
/**
 * 为占位素材初始化分片上传。
 * @param input 素材 ID、文件名、大小和可选 MIME
 * @returns 任务号、对象键和分片大小
 */
export async function initAssetMultipartUpload(input) {
    const response = await httpClient.post('/asset/multipart/init', input);
    return multipartInitSchema.parse(readApiEnvelopeData(response));
}
/** 经统一鉴权客户端上传一片，不向浏览器暴露 OSS 签名地址。 */
export async function uploadAssetMultipartPart(input) {
    const formData = new FormData();
    formData.append('assetId', String(input.assetId));
    formData.append('uploadId', input.uploadId);
    formData.append('objectKey', input.objectKey);
    formData.append('partNumber', String(input.partNumber));
    formData.append('file', input.file, `part-${String(input.partNumber)}`);
    const response = await httpClient.post('/asset/uploadMultipartPart', formData, {
        signal: input.signal,
    });
    return multipartUploadPartSchema.parse(readApiEnvelopeData(response));
}
/**
 * 合并分片并把文件地址写回占位素材。
 * @param input 分片清单、文件大小和可选时长
 * @returns 已有文件地址的素材
 */
export async function completeAssetMultipartUpload(input) {
    const response = await httpClient.post('/asset/multipart/complete', input);
    return assetItemSchema.parse(readApiEnvelopeData(response));
}
/**
 * 中止未完成的分片任务，不删除占位素材。
 * @param input 素材、任务号和对象键
 */
export async function abortAssetMultipartUpload(input) {
    await httpClient.post('/asset/multipart/abort', input);
}
/**
 * 在素材库中新建一条没有文件的素材记录（空位），等结果填入后补上文件。
 *
 * 服务端上传接口允许不传文件，但落库要分两步：
 * 1. `/asset/upload` 建记录，落类型、名称、分类、主体与项目；
 * 2. `/asset/update` 补写生成提示词——上传接口的入参里没有这个字段，
 *    而服务端只在素材来源为「1-AI 生成」时才保存与返回生成提示词。
 *
 * 因此第一步必须按 AI 生成提交；第二步是整份覆盖保存，必须把分类、主体与项目一并回传，
 * 否则这三个字段会被清空（素材会掉出项目）。
 *
 * @param input 占位素材的元数据。
 * @returns 新建的素材记录；补写提示词后返回最新值。
 */
export async function createPlaceholderAsset(input) {
    /** 第一步创建的记录；接口单条提交时只返回一条。 */
    const created = await uploadAssets({
        assetType: GENERATED_ASSET_SOURCE_TYPE,
        assetDataType: input.assetDataType,
        assetCategory: input.assetCategory,
        ...(input.assetSubjectType == null ? {} : { assetSubjectType: input.assetSubjectType }),
        projectId: input.projectId,
        assetName: input.assetName,
    });
    // 服务端未返回记录说明建档失败，这里直接报错，避免调用方拿到空值继续走。
    if (created.length === 0)
        throw new Error('素材创建失败，请稍后重试');
    /** 新建的素材记录。 */
    const asset = created[0];
    /** 用户在弹窗里预填的生成要求。 */
    const generationPrompt = input.generationPrompt?.trim() ?? '';
    if (generationPrompt === '')
        return asset;
    return updateAsset({
        id: asset.id,
        assetName: asset.assetName?.trim() ?? input.assetName,
        assetCategory: input.assetCategory,
        assetSubjectType: input.assetSubjectType,
        projectId: input.projectId,
        generationPrompt,
    });
}
/**
 * 替换素材文件，服务端会同步更新文件地址、封面、媒体类型并重置视频缩略图。
 * @param input 素材 ID 与新文件信息或已有 OSS 地址。
 * @returns 替换后的完整素材实体。
 */
export async function replaceAssetFile(input) {
    const formData = new FormData();
    formData.append('id', String(input.id));
    if (input.file != null)
        formData.append('file', input.file, input.file.name);
    if (input.assetUrl != null)
        formData.append('assetUrl', input.assetUrl);
    formData.append('assetDataType', input.assetDataType);
    formData.append('assetCategory', input.assetCategory);
    if (input.assetSubjectType)
        formData.append('assetSubjectType', input.assetSubjectType);
    if (input.taskId)
        formData.append('taskId', input.taskId);
    const response = await httpClient.post('/asset/file/update', formData);
    return assetItemSchema.parse(readApiEnvelopeData(response));
}
/**
 * 把当前用户 OSS 目录里已有的对象登记为素材，不重复上传或复制文件。
 * 生成结果与前端合成结果落进素材库都走这里。
 * @param input 素材元数据与待登记对象。
 * @returns 新建的素材记录。
 */
export async function importOssAssets(input) {
    const response = await httpClient.post('/asset/import-oss', input);
    return assetItemSchema.array().parse(readApiEnvelopeData(response));
}
/** 分页查询当前用户素材回收站，仅返回仍在 30 天保留期内的素材。 */
export async function fetchAssetRecyclePage(query) {
    const response = await httpClient.get('/asset/recycle/page', { params: query });
    return assetRecyclePageSchema.parse(readApiEnvelopeData(response));
}
/** 批量恢复素材回收站中的素材；整批事务，任一条不可恢复则全部不恢复。 */
export async function restoreRecycledAssets(ids) {
    await httpClient.post('/asset/recycle/restore', { ids });
}
/** 清空当前用户素材回收站，返回服务端实际清理的素材数量。 */
export async function clearAssetRecycleBin() {
    const response = await httpClient.post('/asset/recycle/clear');
    const data = readApiEnvelopeData(response);
    return typeof data === 'number' ? data : 0;
}
/**
 * 删除前重新确认一批素材的引用数量。
 *
 * 服务端删除接口不做引用校验，拦截完全落在前端，因此删除前重新取一次数据，
 * 避免用户刚在编辑器断开引用、这边因为列表数据陈旧而误拦。
 *
 * 取数策略：
 * - 单条：按素材名查询该项目素材，命中后按 ID 精确取最新引用数量；
 * - 多条：只拉一页素材列表（单页上限 100 条）后按 ID 匹配，避免逐个请求；
 * 未命中的素材不会出现在返回值里，由调用方沿用本地已知数量兜底。
 *
 * @param targets 待确认的素材。
 * @returns 素材 ID 到最新引用数量的映射；只包含成功匹配到的素材。
 * @throws 请求失败时抛出接口错误，调用方需要自行兜底（沿用本地数据）。
 */
export async function fetchAssetUsageCounts(targets) {
    const counts = new Map();
    if (targets.length === 0)
        return counts;
    if (targets.length === 1) {
        const [single] = targets;
        const assetName = single.assetName?.trim();
        if (assetName !== undefined && assetName !== '') {
            const page = await fetchAssetPage({
                assetName,
                projectId: single.projectId,
                pageNum: 1,
                pageSize: PROJECT_MATERIAL_PAGE_SIZE,
            });
            const matched = page.records.find((record) => record.id === single.id);
            if (matched)
                counts.set(single.id, matched.references?.length ?? 0);
            return counts;
        }
    }
    /** 同项目时收窄查询范围，跨项目（素材中心）时不带项目条件。 */
    const projectIds = new Set(targets.map((target) => target.projectId ?? 0));
    const page = await fetchAssetPage({
        pageNum: 1,
        pageSize: PROJECT_MATERIAL_PAGE_SIZE,
        ...(projectIds.size === 1 ? { projectId: [...projectIds][0] } : {}),
    });
    for (const target of targets) {
        const matched = page.records.find((record) => record.id === target.id);
        if (matched)
            counts.set(target.id, matched.references?.length ?? 0);
    }
    return counts;
}
/** 重命名当前项目素材库中的单个素材。 */
export async function renameProjectMaterial(projectId, id, assetName) {
    // 项目素材库与素材中心共用同一份素材数据，重命名统一走素材重命名接口。
    await renameAsset(id, assetName);
}
/** 批量查询素材视频时间轴缩略图状态；无记录的素材不出现在结果中。 */
export async function fetchAssetThumbnails(assetIds) {
    const response = await httpClient.get('/asset/thumbnails', {
        params: { assetIds: assetIds.join(',') },
    });
    return assetThumbnailSchema.array().parse(readApiEnvelopeData(response));
}
/** 为缺少缩略图记录的视频素材补建抽帧任务，返回全部入参素材的状态。 */
export async function ensureAssetThumbnails(assetIds) {
    const response = await httpClient.post('/asset/thumbnails/ensure', { assetIds });
    return assetThumbnailSchema.array().parse(readApiEnvelopeData(response));
}
/** 重试失败的素材缩略图任务，返回最新状态。 */
export async function retryAssetThumbnail(assetId) {
    const response = await httpClient.post('/asset/thumbnails/retry', { id: assetId });
    return assetThumbnailSchema.parse(readApiEnvelopeData(response));
}
/**
 * 把分片上传期间截好的本地胶片帧交给服务端拼接。
 * 帧必须按整段中点从左到右排列。已经就绪时服务端直接返回现状。
 *
 * @param assetId 视频素材 ID
 * @param frames 按中点顺序排列的 JPEG
 * @returns 最新缩略图状态
 */
export async function submitAssetFilmstripFrames(assetId, frames) {
    /** 按顺序提交的胶片帧。字段名与服务端入参一致。 */
    const form = new FormData();
    form.append('assetId', String(assetId));
    frames.forEach((frame, index) => {
        form.append('frames', frame, `frame-${String(index)}.jpg`);
    });
    const response = await httpClient.post('/asset/thumbnails/frames', form);
    return assetThumbnailSchema.parse(readApiEnvelopeData(response));
}
